/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ProductImage from '../components/ProductImage';
import API_BASE_URL from '../config/apiConfig';
import { deriveRatingWidth } from '../utils/productRating';
import { isFreeDelivery } from '../utils/productHelpers';

const FALLBACK_HERO_SLIDES = [
  { BannerId: 'fallback-hero-1', ImageUrl: '/assets/imgs/slider/slider1.jpg' },
  { BannerId: 'fallback-hero-2', ImageUrl: '/assets/imgs/slider/slider2.jpg' },
];

const FALLBACK_PROMO_BANNERS = [
  {
    BannerId: 'fallback-promo-1',
    ImageUrl: '/assets/imgs/banner/heroa12mp.jpg',
    Title: 'DAHUA Hero A1\nIndoor PAN/TILT\nWiFi 2MP',
    ProductId: 13,
  },
  {
    BannerId: 'fallback-promo-2',
    ImageUrl: '/assets/imgs/banner/rangers2.jpg',
    Title: 'Imou Ranger S2\nWiFi Security\nCamera 3MP',
    ProductId: 14,
  },
  {
    BannerId: 'fallback-promo-3',
    ImageUrl: '/assets/imgs/banner/ezvizwifilite.jpg',
    Title: 'EZVIZ CP1 Lite\nSmart Wi-Fi Indoor\nSecurity Camera 2MP',
    ProductId: 15,
  },
];

const resolveBannerImageUrl = (imageUrl) => {
  const raw = String(imageUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('/')) return raw;
  return `/${raw.replace(/^\/+/, '')}`;
};

const renderPromoTitle = (title) => {
  const lines = String(title || '').split('\n');
  return lines.map((line, index) => (
    <React.Fragment key={`${line}-${index}`}>
      {index > 0 ? <br /> : null}
      {line}
    </React.Fragment>
  ));
};

const HomeProductCard = ({ product, index, getAnimationDelay, formatPrice, onBuyNow }) => (
  <div className="col-lg-1-5 col-md-4 col-12 col-sm-6">
    <div className="product-cart-wrap mb-30 wow animate__animated animate__fadeIn" data-wow-delay={getAnimationDelay(index)}>
      <div className="product-img-action-wrap">
        <div className="product-img product-img-zoom">
          <a href={`/product-detail/${product.ProductId}`}>
            <ProductImage className="default-img" src={product.DefaultImage} alt={product.ProductName} />
            <ProductImage className="hover-img" src={product.HoverImage || product.DefaultImage} alt={product.ProductName} />
          </a>
        </div>
        {product.BadgeLabel ? (
          <div className="product-badges product-badges-position product-badges-mrg">
            <span className={product.BadgeClass || 'hot'}>{product.BadgeLabel}</span>
          </div>
        ) : null}
      </div>
      <div className="product-content-wrap">
        <div className="product-category">
          <a href="shop-grid-right">{product.SubCategoryName}</a>
        </div>
        <h2><Link to={`/product-detail/${product.ProductId || 1}`}>{product.ProductName}</Link></h2>
        <div className="product-rate-cover">
          <div className="product-rate d-inline-block">
            <div className="product-rating" style={{ width: deriveRatingWidth(product.Rating) }}></div>
          </div>
          <span className="font-small ml-5 text-muted"> ({product.Rating || 0})</span>
        </div>
        {isFreeDelivery(product) ? (
          <div>
            <span className="font-small text-muted"><a href="#">Free Delivery</a></span>
          </div>
        ) : null}
        <div className="product-card-bottom">
          <div className="product-price">
            <span><i className="fa fa-inr fs-10" aria-hidden="true"></i>{formatPrice(product.Price)}</span>
            <span className="old-price"><i className="fa fa-inr fs-10" aria-hidden="true"></i>{formatPrice(product.OldPrice)}</span>
          </div>
          <div className="add-cart">
            <a className="add" onClick={() => onBuyNow(product)}><i className="fi-rs-shopping-cart mr-5"></i>Buy Now</a>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState(FALLBACK_HERO_SLIDES);
  const [promoBanners, setPromoBanners] = useState(FALLBACK_PROMO_BANNERS);
  const [sections, setSections] = useState([]);
  const navigate = useNavigate();
  const { addToCart, clearCart } = useCart();
  const { isAuthenticated } = useAuth();

  const slideCount = heroSlides.length;

  const handleBuyNow = (product) => {
    if (!product) {
      return;
    }

    if (!isAuthenticated) {
      clearCart();
    }

    addToCart(product, 1);
    navigate('/shop-cart');
  };

  const visibleSections = sections.filter(
    (section) => (section.Products || []).length > 0,
  );

  const visiblePromoBanners = useMemo(
    () => promoBanners,
    [promoBanners],
  );

  const getAnimationDelay = (index) => {
    const delays = ['.1s', '.2s', '.3s', '.4s', '.5s'];
    return delays[index % delays.length];
  };

  const formatPrice = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
      return value;
    }
    return number.toString();
  };

  useEffect(() => {
    if (!slideCount) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveSlide((previous) => (previous + 1) % slideCount);
    }, 4000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [slideCount]);

  useEffect(() => {
    const $ = window.jQuery;
    if (!$) return undefined;

    const $slider = $('.home-slide-cover .hero-slider-1');
    if ($slider.length && $slider.hasClass('slick-initialized')) {
      $slider.slick('unslick');
    }

    return undefined;
  }, [heroSlides.length]);

  useEffect(() => {
    if (activeSlide >= slideCount) {
      setActiveSlide(0);
    }
  }, [activeSlide, slideCount]);

  const goToPreviousSlide = () => {
    if (!slideCount) return;
    setActiveSlide((previous) => (previous - 1 + slideCount) % slideCount);
  };

  const goToNextSlide = () => {
    if (!slideCount) return;
    setActiveSlide((previous) => (previous + 1) % slideCount);
  };

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        const [bannersResponse, sectionsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/product/homepageBanners`),
          fetch(`${API_BASE_URL}/product/homepageProducts`),
        ]);

        const bannersResult = await bannersResponse.json();
        if (bannersResponse.ok && bannersResult?.success && bannersResult?.data) {
          const apiHeroSlides = Array.isArray(bannersResult.data.heroSlides)
            ? bannersResult.data.heroSlides
            : [];
          const apiPromoBanners = Array.isArray(bannersResult.data.promoBanners)
            ? bannersResult.data.promoBanners
            : [];

          if (apiHeroSlides.length > 0) {
            setHeroSlides(apiHeroSlides);
          }
          if (apiPromoBanners.length > 0) {
            setPromoBanners(apiPromoBanners);
          }
        }

        const sectionsResult = await sectionsResponse.json();
        if (sectionsResponse.ok && sectionsResult?.success && Array.isArray(sectionsResult.data) && sectionsResult.data.length > 0) {
          setSections(sectionsResult.data);
        }
      } catch (error) {
        console.error('Failed to load homepage data:', error);
      }
    };

    fetchHomepageData();
  }, []);

  return (
    <main className="main">
      {slideCount > 0 ? (
        <section className="home-slider position-relative mb-30">
          <div className="container">
            <div className="home-slide-cover mt-10">
              <div className="hero-slider-1 style-4 dot-style-1 dot-style-1-position-1">
                {heroSlides.map((slide, index) => (
                  <div
                    key={slide.BannerId || slide.ImageUrl}
                    className="single-hero-slider single-animation-wrap"
                    style={{
                      backgroundImage: `url(${resolveBannerImageUrl(slide.ImageUrl)})`,
                      display: index === activeSlide ? 'block' : 'none',
                    }}
                  />
                ))}
                {slideCount > 1 ? (
                  <ul className="hero-slider-dots">
                    {heroSlides.map((slide, index) => (
                      <li
                        key={`dot-${slide.BannerId || slide.ImageUrl}-${index}`}
                        className={index === activeSlide ? 'slick-active' : ''}
                      >
                        <button
                          type="button"
                          aria-label={`Go to slide ${index + 1}`}
                          onClick={() => setActiveSlide(index)}
                        />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              {slideCount > 1 ? (
                <div className="slider-arrow hero-slider-1-arrow">
                  <span className="slider-btn slider-prev slick-arrow" onClick={goToPreviousSlide} role="button" tabIndex={0} onKeyDown={(event) => event.key === 'Enter' && goToPreviousSlide()}>
                    <i className="fi-rs-arrow-small-left"></i>
                  </span>
                  <span className="slider-btn slider-next slick-arrow" onClick={goToNextSlide} role="button" tabIndex={0} onKeyDown={(event) => event.key === 'Enter' && goToNextSlide()}>
                    <i className="fi-rs-arrow-small-right"></i>
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {visiblePromoBanners.length > 0 ? (
        <section className="banners mb-25">
          <div className="container">
            <div className="row">
              {visiblePromoBanners.map((banner, index) => {
                const columnClass = index === 2
                  ? 'col-lg-4 d-md-none d-lg-flex'
                  : 'col-lg-4 col-md-6';
                const wowDelay = index === 0 ? '0' : `${index * 0.2}s`;
                const productLink = banner.ProductId
                  ? `/product-detail/${banner.ProductId}`
                  : 'shop-grid-right';

                return (
                  <div key={banner.BannerId || banner.ImageUrl} className={columnClass}>
                    <div
                      className={`banner-img wow animate__animated animate__fadeInUp${index === 2 ? ' mb-sm-0' : ''}`}
                      data-wow-delay={wowDelay}
                    >
                      <img src={resolveBannerImageUrl(banner.ImageUrl)} alt="" />
                      <div className="banner-text">
                        <h4>{renderPromoTitle(banner.Title)}</h4>
                        <Link to={productLink} className="btn btn-xs">
                          Shop Now <i className="fi-rs-arrow-small-right"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {visibleSections.map((section) => (
        <section key={section.ProductHomePageSectionId || section.SectionName} className="product-tabs section-padding position-relative">
          <div className="container">
            <div className="section-title style-2 wow animate__animated animate__fadeIn">
              <h3>{section.SectionName}</h3>
            </div>
            <div className="tab-content" id="myTabContent">
              <div className="tab-pane fade show active" id="tab-one" role="tabpanel" aria-labelledby="tab-one">
                <div className="row product-grid-4">
                  {(section.Products || []).map((product, index) => (
                    <HomeProductCard
                      key={product.ProductId || `${section.SectionName}-${index}`}
                      product={product}
                      index={index}
                      getAnimationDelay={getAnimationDelay}
                      formatPrice={formatPrice}
                      onBuyNow={handleBuyNow}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="featured section-padding">
        <div className="container">
          <div className="row">
            <div className="col-lg-1-3 col-md-3 col-12 col-sm-6 mb-md-4 mb-xl-0">
              <div className="banner-left-icon d-flex align-items-center wow animate__animated animate__fadeInUp" data-wow-delay="0">
                <div className="banner-icon">
                  <img src="assets/imgs/theme/icons/icon-1.svg" alt="" />
                </div>
                <div className="banner-text">
                  <h3 className="icon-box-title">Best prices & offers</h3>
                  <p>Orders $50 or more</p>
                </div>
              </div>
            </div>
            <div className="col-lg-1-3 col-md-3 col-12 col-sm-6">
              <div className="banner-left-icon d-flex align-items-center wow animate__animated animate__fadeInUp" data-wow-delay=".1s">
                <div className="banner-icon">
                  <img src="assets/imgs/theme/icons/icon-2.svg" alt="" />
                </div>
                <div className="banner-text">
                  <h3 className="icon-box-title">Free delivery</h3>
                  <p>24/7 amazing services</p>
                </div>
              </div>
            </div>
            <div className="col-lg-1-3 col-md-3 col-12 col-sm-6">
              <div className="banner-left-icon d-flex align-items-center wow animate__animated animate__fadeInUp" data-wow-delay=".2s">
                <div className="banner-icon">
                  <img src="assets/imgs/theme/icons/icon-3.svg" alt="" />
                </div>
                <div className="banner-text">
                  <h3 className="icon-box-title">Great daily deal</h3>
                  <p>When you sign up</p>
                </div>
              </div>
            </div>
            <div className="col-lg-1-3 col-md-3 col-12 col-sm-6">
              <div className="banner-left-icon d-flex align-items-center wow animate__animated animate__fadeInUp" data-wow-delay=".3s">
                <div className="banner-icon">
                  <img src="assets/imgs/theme/icons/icon-4.svg" alt="" />
                </div>
                <div className="banner-text">
                  <h3 className="icon-box-title">Wide assortment</h3>
                  <p>Mega Discounts</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
